// initNativeKeyboard.js — VIN-2026-64 (2026-05-28)
//
// Vinarai uses the REAL native keyboard everywhere — it's a Capacitor
// WKWebView (iOS) / WebView (Android), so every <input>/<textarea> brings
// up the system keyboard. There is no custom keyboard in the app.
//
// What was missing: a dismiss affordance. On iOS the "Done" button lives
// on the keyboard's INPUT ACCESSORY BAR (the toolbar that sits just above
// the keys with ‹ › navigation + Done). Capacitor's Keyboard plugin JS API
// was never called, so the bar sat at its default and users had no obvious
// way to close the keyboard from a text field (tapping return inserts a
// newline in a textarea; it doesn't dismiss).
//
// Fix: explicitly enable the accessory bar on native startup. One call,
// app-wide — every input gets the native Done button for free, no
// per-field wiring. iOS-only by design:
//   • iOS  → shows the ‹ › / Done toolbar above the keyboard.
//   • Android → setAccessoryBarVisible is a no-op; the platform keyboard
//     already carries its own dismiss (the system back / down-chevron),
//     so nothing more is needed there.
//
// Best-effort: dynamically imports the plugin so the web bundle never
// pulls it in, and swallows any error (a missing plugin or older runtime
// just leaves the keyboard at its default — no crash).

function isNativePlatform() {
  return typeof window !== 'undefined'
    && typeof window.Capacitor?.isNativePlatform === 'function'
    && window.Capacitor.isNativePlatform()
}

export function initNativeKeyboard() {
  if (!isNativePlatform()) return
  try {
    // VIN-2026-63 hotfix: use the globally-registered Capacitor plugin
    // proxy instead of `await import('@capacitor/keyboard')`. The dynamic
    // import created a boot-time JS chunk whose modulepreload can fail in
    // the WKWebView — and because that fires `vite:preloadError`, the
    // reload handler in main.jsx turned it into a white-screen reload
    // loop on launch. The global proxy is already present once Capacitor's
    // runtime injects (no chunk to preload), and no-ops cleanly if the
    // Keyboard plugin isn't registered. Show the native input-accessory
    // toolbar (the "Done" bar). iOS-only; harmless elsewhere.
    const Keyboard = window.Capacitor?.Plugins?.Keyboard
    // VIN-2026-79: HIDE the input-accessory bar. We previously SHOWED it for a
    // native "Done" button (VIN-2026-64), but that bar is iOS's system
    // inputAccessoryView — its tint does NOT reliably follow Keyboard.setStyle,
    // so on the light/ivory theme it renders as a black slab above the keys
    // that we couldn't theme from JS. Hiding it removes the slab entirely
    // (setHideFormAccessoryBar swizzles inputAccessoryView → nil). The keyboard
    // still dismisses by tapping outside any field (WKWebView blurs the input),
    // and the bottom nav already collapses on keyboard-open, so no chrome is
    // lost. A themed Done bar would require a custom native inputAccessoryView
    // (tracked as #169) — deferred; removing the black bar is the priority.
    Keyboard?.setAccessoryBarVisible?.({ isVisible: false })?.catch?.(() => {})
  } catch (err) {
    console.debug('[initNativeKeyboard] skipped:', err?.message || err)
  }
}
